import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosInstance";
import { toast } from "react-hot-toast";

const MembershipDetails = () => {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();

  const fetchProfile = async () => {
    try {
      const res = await api.get("/profile");
      const memberData = res.data.member || res.data;
      setMember(memberData);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load membership details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "APPROVED_AWAITING_PAYMENT":
        return "bg-yellow-100 text-yellow-800";
      case "PENDING_ADMIN_APPROVAL":
        return "bg-blue-100 text-blue-800";
      case "PENDING_VERIFICATION":
        return "bg-gray-100 text-gray-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "SUCCESS":
        return "bg-green-100 text-green-800";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "FAILED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getMembershipBenefits = (category, type) => {
    const benefits = {
      FULL_MEMBERSHIP: [
        "Unlimited golf course access",
        "Priority tee time booking",
        "Access to all club facilities",
        "Complimentary guest passes (4 per month)",
        "Discounts on pro shop purchases",
        "Invitation to member-only events",
        "Locker room access",
      ],
      TEMPORARY_MEMBERSHIP: [
        "Limited golf course access (weekdays only)",
        "Basic facility access",
        "2 guest passes per month",
        "Pro shop discounts (10%)",
        "Event participation (additional fees may apply)",
      ],
      CORPORATE_MEMBERSHIP: [
        "Multiple user access (up to 5 employees)",
        "Business event hosting privileges",
        "Corporate branding opportunities",
        "Priority booking for corporate events",
        "Complimentary meeting room usage",
        "Enhanced guest passes",
      ],
      HONORARY_MEMBERSHIP: [
        "Full golf course access",
        "All club facilities access",
        "Complimentary guest privileges",
        "VIP event invitations",
        "Lifetime locker assignment",
      ],
    };

    return (
      benefits[type] || [
        "Basic golf course access",
        "Standard facility usage",
        "Member event participation",
      ]
    );
  };

  const getCategoryDescription = (category) => {
    const descriptions = {
      SERVING_DEFENCE_OFFICER:
        "Active military personnel serving in defence forces",
      RETIRED_DEFENCE_OFFICER:
        "Honorary membership for retired defence personnel",
      GOVERNMENT_OFFICER: "Civil servants and government employees",
      NON_MILITARY: "Civilian members from various professions",
      CORPORATE_BUSINESS: "Business entities and corporate representatives",
      FOREIGNER_DIPLOMAT: "International members and diplomatic personnel",
    };
    return descriptions[category] || "Golf club membership";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading membership details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-2"
              >
                <svg
                  className="w-3 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Dashboard
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                Membership Details
              </h1>
              <p className="text-gray-600">
                Your RGCC membership information and benefits
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Membership Overview Card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {member.firstName} {member.lastName}
              </h2>
              <p className="text-gray-600 mt-1">Member ID: {member.userId}</p>
              <p className="text-gray-500 text-sm mt-1">
                {getCategoryDescription(member.membershipCategory)}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                  member.registrationStatus
                )}`}
              >
                {member.registrationStatus.replace(/_/g, " ")}
              </span>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(
                  member.paymentStatus
                )}`}
              >
                Payment: {member.paymentStatus}
              </span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab("overview")}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                  activeTab === "overview"
                    ? "border-green-500 text-green-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab("benefits")}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                  activeTab === "benefits"
                    ? "border-green-500 text-green-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Benefits
              </button>
              <button
                onClick={() => setActiveTab("documents")}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                  activeTab === "documents"
                    ? "border-green-500 text-green-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Documents
              </button>
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Membership Status Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Membership Status
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Membership Category</p>
                  <p className="font-semibold text-gray-900">
                    {member.membershipCategory}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Membership Type</p>
                  <p className="font-semibold text-gray-900">
                    {member.membershipType}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Registration Status</p>
                  <p className="font-semibold text-gray-900">
                    {member.registrationStatus.replace(/_/g, " ")}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Payment Status</p>
                  <p
                    className={`font-semibold ${
                      member.paymentStatus === "SUCCESS"
                        ? "text-green-600"
                        : member.paymentStatus === "PENDING"
                        ? "text-yellow-600"
                        : "text-red-600"
                    }`}
                  >
                    {member.paymentStatus}
                  </p>
                </div>
              </div>
            </div>

            {/* Membership Timeline */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Membership Journey
              </h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">
                      Registration Submitted
                    </p>
                    <p className="text-sm text-gray-500">
                      Your membership application has been received
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      member.registrationStatus === "PENDING_VERIFICATION"
                        ? "bg-gray-100"
                        : "bg-green-100"
                    }`}
                  >
                    {member.registrationStatus !== "PENDING_VERIFICATION" ? (
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    )}
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">
                      Email Verification
                    </p>
                    <p className="text-sm text-gray-500">
                      Email address verified successfully
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      [
                        "PENDING_ADMIN_APPROVAL",
                        "APPROVED_AWAITING_PAYMENT",
                        "ACTIVE",
                      ].includes(member.registrationStatus)
                        ? "bg-green-100"
                        : "bg-gray-100"
                    }`}
                  >
                    {["APPROVED_AWAITING_PAYMENT", "ACTIVE"].includes(
                      member.registrationStatus
                    ) ? (
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : member.registrationStatus ===
                      "PENDING_ADMIN_APPROVAL" ? (
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                    ) : (
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    )}
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">
                      Admin Approval
                    </p>
                    <p className="text-sm text-gray-500">
                      {member.registrationStatus === "PENDING_ADMIN_APPROVAL"
                        ? "Awaiting administrative review"
                        : "Application approved by administration"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      member.registrationStatus === "ACTIVE"
                        ? "bg-green-100"
                        : member.registrationStatus ===
                          "APPROVED_AWAITING_PAYMENT"
                        ? "bg-yellow-100"
                        : "bg-gray-100"
                    }`}
                  >
                    {member.registrationStatus === "ACTIVE" ? (
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : member.registrationStatus ===
                      "APPROVED_AWAITING_PAYMENT" ? (
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                    ) : (
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    )}
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">
                      Payment & Activation
                    </p>
                    <p className="text-sm text-gray-500">
                      {member.registrationStatus === "ACTIVE"
                        ? "Payment completed and membership activated"
                        : member.registrationStatus ===
                          "APPROVED_AWAITING_PAYMENT"
                        ? "Complete payment to activate membership"
                        : "Pending previous steps"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            {member.registrationStatus === "APPROVED_AWAITING_PAYMENT" && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-yellow-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-yellow-800">
                      Payment Required
                    </h3>
                    <p className="text-yellow-700 mt-1">
                      Your membership has been approved! Complete the payment to
                      activate your membership and unlock all features.
                    </p>
                    <button
                      onClick={() => navigate("/payment-checkout")}
                      className="mt-3 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition"
                    >
                      Complete Payment
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Benefits Tab */}
        {activeTab === "benefits" && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Membership Benefits
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Included Benefits */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-4">
                  Your Benefits
                </h4>
                <ul className="space-y-3">
                  {getMembershipBenefits(
                    member.membershipCategory,
                    member.membershipType
                  ).map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <svg
                        className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Membership Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">
                  Membership Information
                </h4>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-600">
                      Category
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {member.membershipCategory}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-600">Type</dt>
                    <dd className="text-sm text-gray-900">
                      {member.membershipType}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-600">
                      Status
                    </dt>
                    <dd className="text-sm">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          member.registrationStatus
                        )}`}
                      >
                        {member.registrationStatus.replace(/_/g, " ")}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-600">
                      Description
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {getCategoryDescription(member.membershipCategory)}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Additional Information */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h4 className="text-md font-semibold text-gray-900 mb-4">
                Additional Information
              </h4>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Some benefits may have usage
                  restrictions or require prior booking. Please contact the club
                  administration for detailed terms and conditions of your
                  membership.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Membership Documents
            </h3>

            <div className="space-y-6">
              {/* Membership Card */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-md font-semibold text-gray-900">
                      Digital Membership Card
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Your virtual membership identification
                    </p>
                  </div>
                  <button
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm"
                    onClick={() =>
                      toast.success(
                        "Membership card download feature coming soon!"
                      )
                    }
                  >
                    Download Card
                  </button>
                </div>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    {member.registrationStatus === "ACTIVE"
                      ? "Your digital membership card is available for download."
                      : "Your digital membership card will be available once your membership is activated."}
                  </p>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-2">
                  Terms & Conditions
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Important documents regarding your membership agreement
                </p>
                <div className="space-y-3">
                  <button
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                    onClick={() =>
                      toast.success(
                        "Membership terms document download coming soon!"
                      )
                    }
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Membership Agreement
                        </p>
                        <p className="text-xs text-gray-500">
                          Terms and conditions of your membership
                        </p>
                      </div>
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                  </button>

                  <button
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                    onClick={() =>
                      toast.success("Club rules document download coming soon!")
                    }
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Club Rules & Regulations
                        </p>
                        <p className="text-xs text-gray-500">
                          Code of conduct and facility usage rules
                        </p>
                      </div>
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                  </button>
                </div>
              </div>

              {/* Uploaded Documents */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">
                  Your Submitted Documents
                </h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 text-center">
                    Document management features coming soon. Your submitted
                    documents are securely stored with the administration.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MembershipDetails;
